import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, XCircle, Skull } from 'lucide-react';
import { MetabolicType } from '../lib/constants';
interface PainPageProps {
  resultType: MetabolicType;
  onContinue: () => void;
}
const PAIN_CONTENT: Record<MetabolicType, {
  title: string;
  subtitle: string;
  bullets: string[];
  closer: string;
}> = {
  jaguar: {
    title: 'TU METABOLISMO JAGUAR ESTÁ DESTRUIDO',
    subtitle: 'Y tú lo estás empeorando cada día.',
    bullets: ['Estás gorda porque comes como si tu cuerpo fuera igual que el de todos. NO LO ES.', 'Cada carbohidrato que metes a tu cuerpo se convierte en GRASA PURA. Tu metabolismo Jaguar RECHAZA lo que comes.', 'Has gastado miles en nutricionistas que te dieron la misma dieta genérica que a todos. ¿Resultado? Sigues IGUAL o PEOR.', 'Esa panza, esos brazos gordos, esa papada... no se van porque estás ALIMENTANDO el problema.', 'Cada día que pasa sin el plan correcto, tu metabolismo se daña MÁS. Y bajar de peso se vuelve MÁS difícil.'],
    closer: 'Tu metabolismo Jaguar necesita proteína y energía controlada. Tú le estás dando basura. Por eso estás así.'
  },
  colibri: {
    title: 'TU METABOLISMO COLIBRÍ ESTÁ EN MODO PÁNICO',
    subtitle: 'Y cada dieta que haces lo destruye más.',
    bullets: ['Pasas hambre TODO el día y sigues GORDA. ¿No te parece absurdo? Tu cuerpo almacena MÁS grasa porque cree que te vas a morir de hambre.', 'Estás cansada, irritable, con ansiedad, sin energía... y la báscula NO SE MUEVE. Es desesperante.', 'Cada dieta restrictiva que haces te deja MÁS GORDA que antes. Es un ciclo de DESTRUCCIÓN que no para.', 'Tu metabolismo Colibrí necesita combustible CONSTANTE. Tú lo estás matando de hambre. Por eso acumulas grasa.', 'Mientras sigas haciendo dietas de revista, tu cuerpo va a seguir en modo supervivencia. ALMACENANDO TODO.'],
    closer: 'Tu metabolismo Colibrí es rápido pero inestable. Necesita comidas frecuentes y específicas. Tú le estás dando hambre. Por eso estás así.'
  },
  oso: {
    title: 'TU METABOLISMO OSO ESTÁ COMPLETAMENTE DAÑADO',
    subtitle: 'Y nadie te ha dicho la verdad.',
    bullets: ['Tu cuerpo almacena TODA la grasa. Comes poco y ENGORDAS. Comes mucho y ENGORDAS. Parece que no hay salida.', 'Tu metabolismo Oso está DAÑADO por años de dietas genéricas que NUNCA fueron hechas para ti.', 'Te sientes pesada, hinchada, OBESA. La ropa no te queda. Te da vergüenza tu propio cuerpo.', 'Has probado TODO: nutricionistas, gym, pastillas, tés mágicos. Y sigues IGUAL O PEOR que antes.', 'Cada kilo que bajas lo recuperas el DOBLE. Tu cuerpo ya no confía en ti. Almacena todo por miedo.'],
    closer: 'Tu metabolismo Oso necesita un equilibrio muy específico. Tú le estás dando caos. Por eso estás así.'
  }
};
export function PainPage({
  resultType,
  onContinue
}: PainPageProps) {
  const content = PAIN_CONTENT[resultType];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -50
  }} className="flex flex-col px-5 pt-6 pb-8 min-h-[100dvh] bg-white max-w-md mx-auto shadow-xl overflow-y-auto hide-scrollbar" data-id="element-209">
      {/* Red warning bar */}
      <div className="w-full bg-red-600 text-white py-2 px-3 rounded-xl mb-6 flex items-center justify-center gap-2" data-id="element-210">
        <Skull className="w-4 h-4" data-id="element-211" />
        <span className="text-xs font-black uppercase tracking-wide" data-id="element-212">
          Esto te va a doler. Pero necesitas leerlo.
        </span>
      </div>

      <h1 className="text-2xl font-black text-red-700 mb-2 leading-tight uppercase tracking-tight" data-id="element-213">
        {content.title}
      </h1>
      <p className="text-lg text-gray-700 font-bold mb-6" data-id="element-214">{content.subtitle}</p>

      <div className="space-y-3 mb-6" data-id="element-215">
        {content.bullets.map((bullet, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: idx * 0.2
      }} className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-3" data-id="element-216">
            <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-217" />
            <p className="text-sm text-gray-800 font-bold leading-relaxed" data-id="element-218">
              {bullet}
            </p>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2
    }} className="bg-dark text-white rounded-2xl p-5 mb-6" data-id="element-219">
        <p className="text-sm font-bold leading-relaxed mb-3" data-id="element-220">
          {content.closer}
        </p>
        <p className="text-yellow-400 font-black text-base leading-snug" data-id="element-221">
          Esto NO es tu culpa. NADIE te enseñó cómo funciona TU metabolismo. Te
          vendieron mentiras. Te hicieron creer que era "fuerza de voluntad". ES
          MENTIRA.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6" data-id="element-222">
        <p className="text-green-800 font-black text-base text-center leading-snug" data-id="element-223">
          Pero hay una solución. Y fue creada ESPECÍFICAMENTE para metabolismos
          tipo <span className="uppercase" data-id="element-224">{resultType}</span>.
        </p>
      </motion.div>

      <motion.button initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.8
    }} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={onContinue} className="w-full bg-red-600 text-white text-base font-black py-4 rounded-xl shadow-[0_5px_0_rgb(185,28,28)] active:shadow-[0_0px_0_rgb(185,28,28)] active:translate-y-[5px] transition-all flex items-center justify-center gap-2 uppercase tracking-tight shrink-0" data-id="element-225">
        QUIERO LA SOLUCIÓN
        <ArrowRight className="w-6 h-6" data-id="element-226" />
      </motion.button>
    </motion.div>;
}